import type { BrowserTab, TabGroup } from '../types';

interface Props {
  tabs: BrowserTab[];
  groups: TabGroup[];
  activeTabId?: string;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onNewTab: () => void;
  onDuplicate: (id: string) => void;
  onTogglePin: (id: string) => void;
  onToggleSearch: () => void;
  onCreateGroup: (tabId: string) => void;
}

export function TabStrip({ tabs, groups, activeTabId, onSelect, onClose, onNewTab, onDuplicate, onTogglePin, onToggleSearch, onCreateGroup }: Props) {
  const ordered = [...tabs].sort((a, b) => Number(b.pinned) - Number(a.pinned));
  const groupFor = (id?: string) => groups.find((group) => group.id === id);

  return (
    <div className="tab-strip" role="tablist" aria-label="Open tabs">
      <div className="tab-scroll fade-overflow">
        {ordered.map((tab) => {
          const group = groupFor(tab.groupId);
          return (
            <div key={tab.id} className="tab-wrap" style={{ ['--group-color' as string]: group?.color || 'transparent' }}>
              {group && <span className="group-rail" title={group.name} />}
              <button
                className={`tab ${tab.id === activeTabId ? 'active' : ''} ${tab.pinned ? 'pinned' : ''}`}
                role="tab"
                aria-selected={tab.id === activeTabId}
                onClick={() => onSelect(tab.id)}
                title={`${tab.title}\n${tab.url}`}
              >
                <img src={tab.favicon || '/icons/mark-circle.png'} alt="" className="favicon" />
                {!tab.pinned && <span className="tab-title">{tab.title || 'New Tab'}</span>}
                {!tab.pinned && tab.status === 'loading' && <span className="tab-spinner" aria-hidden="true" />}
              </button>
              <div className={tab.pinned ? 'tab-actions pinned-actions' : 'tab-actions'}>
                {!tab.pinned && <button aria-label="Duplicate tab" onClick={() => onDuplicate(tab.id)}>⧉</button>}
                <button aria-label={tab.pinned ? 'Unpin tab' : 'Pin tab'} onClick={() => onTogglePin(tab.id)}>{tab.pinned ? '↧' : '⌖'}</button>
                {!tab.pinned && <button aria-label="Create group" onClick={() => onCreateGroup(tab.id)}>▣</button>}
                <button aria-label="Close tab" onClick={() => onClose(tab.id)}>×</button>
              </div>
            </div>
          );
        })}
      </div>
      <button className="tab-tool" onClick={onNewTab} title="New tab">＋</button>
      <button className="tab-tool" onClick={onToggleSearch} title="Search tabs">⌕</button>
    </div>
  );
}
